import React, { useMemo } from 'react'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'

import {
  Typography,
  Card,
  VerticalStepper,
  WindowTitle,
} from '@homeviews/shared-ui'

import type { ITheme } from '@homeviews/styles'
import { trackCompanyProgress } from '../helpers/trackCompanyProgress'

import { getCompanyStatus } from '@homeviews/store'

const PROGRESS_DATA = [
  {
    id: 1,
    key: 'description',
    group: 'company_status',
    title: 'Add description',
    titleInactive: 'Add description',
    subtitle:
      'Congratulations you have added a description on your company profile',
    subtitleInactive: 'Add a description on your company profile',
  },
  {
    id: 2,
    key: 'logo',
    group: 'company_status',
    title: 'Add logo',
    titleInactive: 'Add logo',
    subtitle: 'Congratulations you have added your company logo',
    subtitleInactive: 'Add your company logo',
  },
  {
    id: 1,
    key: 'images',
    group: 'development_status',
    title: 'Add development images',
    titleInactive: 'Add development images',
    subtitle: 'Congratulations you have added images to your development',
    subtitleInactive: 'Add images to your development',
  },
  {
    id: 2,
    key: 'facilities',
    group: 'development_status',
    title: 'Add development facilities',
    titleInactive: 'Add development facilities',
    subtitle: 'Congratulations you have added facilities to your development',
    subtitleInactive: 'Add available facilities to your development',
  },
  {
    id: 3,
    key: 'description',
    group: 'development_status',
    title: 'Add development description',
    titleInactive: 'Add development description',
    subtitle:
      'Congratulations you have added a description to your development',
    subtitleInactive: 'Add a description to your development',
  },
  {
    id: 4,
    key: 'replies',
    group: 'development_status',
    title: 'Respond to reviews',
    titleInactive: 'Respond to reviews',
    subtitle: 'Congratulations you have added your first response',
    subtitleInactive: 'Respond to your first review',
  },
]

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    root: {
      padding: theme.spacing(
        theme.layout.contentHorizontalPadding,
        theme.layout.contentVerticalPadding,
      ),
    },
    card: {
      padding: 0,
    },
    topContent: {
      position: 'relative',
      backgroundColor: '#F7FAFC',
      minHeight: 300,
      overflow: 'hidden',
    },
    topContentInner: {
      position: 'relative',
      zIndex: 1,
      padding: 40,
      [theme.breakpoints.down('sm')]: {
        padding: 30,
      },
    },
    introTitle: {
      fontSize: '34px',
      fontWeight: 500,
      letterSpacing: '-0.21px',
      lineHeight: '43px',
      marginBottom: 20,
      '& span': {
        color: theme.colors.primary,
      },
    },
    introText: {
      color: theme.colors.grey800,
      fontSize: 22,
      letterSpacing: '-0.22px',
      lineHeight: '34px',
      marginTop: 15,
    },
    bgImage: {
      marginTop: 'auto',
      marginLeft: 'auto',
      maxWidth: 550,
      [theme.breakpoints.down('sm')]: {
        maxWidth: 'initial',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'left',
      },
    },
    bottomContent: {
      padding: 40,
      [theme.breakpoints.down('sm')]: {
        padding: 30,
      },
    },
    stepperTitle: {
      fontSize: 21,
      letterSpacing: '-0.2px',
      lineHeight: '26px',
      marginBottom: 5,
      fontWeight: 500,
    },
    stepperSubtitle: {
      color: '#4E516B',
      marginBottom: 30,
    },
    topImgWrapper: {
      position: 'relative',
      display: 'flex',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        height: 324,
      },
    },
  }),
)

const Home: React.FC = () => {
  const classes = useStyles()
  const companyStatusData = useSelector(getCompanyStatus)

  const data = useMemo(
    () => trackCompanyProgress(PROGRESS_DATA, companyStatusData),
    [companyStatusData],
  )

  return (
    <>
      <WindowTitle title="Home" />
      <div className={classes.root}>
        <Typography variant="h1">Getting Started</Typography>
        <Card className={classes.card}>
          <div className={classes.topContent}>
            <Grid container spacing={0}>
              <Grid item md={6}>
                <div className={classes.topContentInner}>
                  <Typography variant="h2" className={classes.introTitle}>
                    Welcome to your portal <span />
                  </Typography>
                  <Typography className={classes.introText}>
                    Your portal gives you direct access to in-depth information
                    about your <Link to="/developments">developments</Link>,{' '}
                    <Link to="reviews">reviews</Link> and manage it all in one
                    easy to use place.
                  </Typography>
                  <Typography className={classes.introText}>
                    Below are some suggestions to make the most of your account
                    and help us make your developments as successful as they can
                    be.
                  </Typography>
                </div>
              </Grid>
              <Grid item md={6} className={classes.topImgWrapper}>
                <img
                  className={classes.bgImage}
                  src="assets/images/getting-started-building.png"
                  alt="Background"
                />
              </Grid>
            </Grid>
          </div>
          <div className={classes.bottomContent}>
            <Grid container spacing={0}>
              <Grid item sm={6}>
                <Typography variant="h3" className={classes.stepperTitle}>
                  Company
                </Typography>
                <Typography className={classes.stepperSubtitle}>
                  Update your company profile such as your name, email address
                  and description.
                </Typography>
                <VerticalStepper steps={data} group="company_status" />
              </Grid>
              <Grid item sm={6}>
                <Typography variant="h3" className={classes.stepperTitle}>
                  Developments
                </Typography>
                <Typography className={classes.stepperSubtitle}>
                  Update your development information.
                </Typography>
                <VerticalStepper steps={data} group="development_status" />
              </Grid>
            </Grid>
          </div>
        </Card>
      </div>
    </>
  )
}

export default Home
